/**
 * La garde de route : hors de `B-01`, il faut une session.
 *
 * **Cette garde ne protège rien, et il faut le lire ici avant de le croire
 * ailleurs.** La protection est **côté serveur** : chaque appel de
 * `/api/backoffice` exige un jeton valide, l'habilitation est vérifiée
 * action par action, et l'adresse d'appel est filtrée avant même la connexion.
 * Un opérateur qui contournerait cette garde — en modifiant l'état du
 * navigateur, par exemple — n'obtiendrait rien de plus qu'une coquille vide qui
 * se remplirait de `401`. Ce qu'elle apporte est du **confort** et de la
 * **défense en profondeur** : elle évite d'afficher une interface qui ne peut
 * rien charger, et elle ramène à la connexion au lieu de laisser un écran de
 * module se peupler de messages d'erreur.
 *
 * Écrit noir sur blanc parce qu'une garde prise pour une protection est
 * exactement le genre de contrôle qui a l'air de couvrir : dans six mois,
 * quelqu'un pourrait relâcher un contrôle serveur en se disant que « l'écran
 * n'est de toute façon pas atteignable ».
 *
 * **La destination voulue est retenue.** Quelqu'un qui vise une file d'alertes
 * et qu'on ramène au tableau de bord après connexion a perdu son geste ; répété
 * cent fois par jour, c'est une charge réelle. L'adresse visée voyage donc dans
 * l'état de navigation, et `B-01` y mène après une connexion réussie.
 */

import type { ReactNode } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { loginPath } from './routes.js';
import { useSession } from '../state/session-context.js';

/** L'état de navigation que la garde transmet à `B-01`. */
export interface IntendedDestinationState {
  /** L'adresse que l'opérateur visait, chemin, requête et fragment compris. */
  readonly from: string;
}

/** L'adresse complète de [location], telle qu'on y reviendra. */
export function destinationOf(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * La destination retenue par la garde, ou `null`.
 *
 * L'état de navigation vient du navigateur : il peut être absent, d'une autre
 * forme, ou porter n'importe quoi. Il est donc **vérifié** plutôt que
 * transtypé, et une valeur qui ne commence pas par une barre oblique est
 * écartée — une adresse absolue posée là mènerait ailleurs que dans cette
 * application.
 */
export function intendedDestinationOf(location: Location): string | null {
  const etat = location.state as Partial<IntendedDestinationState> | null | undefined;
  const cible = etat?.from;
  if (typeof cible !== 'string') return null;
  if (!cible.startsWith('/') || cible.startsWith('//')) return null;
  if (cible === loginPath) return null;
  return cible;
}

/** Rend les routes filles si une session est ouverte, sinon mène à `B-01`. */
export function RequireSession(): ReactNode {
  const operator = useSession((etat) => etat.operator);
  const location = useLocation();
  if (operator === null) {
    return <Navigate replace state={{ from: destinationOf(location) }} to={loginPath} />;
  }
  return <Outlet />;
}
